import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useStateContext } from "contexts/auth-context";

import GoodsIssueTab from "./GoodsIssueTab";
import DocumentInfoTab from "./DocumentInfoTab";
import { ActivityLog } from "components/UI/ActivityLog";
import CustomizedTab from "components/Custom/CustomizedTab";
import BreadcrumbsLayout from "components/UI/BreadcrumbsLayout";
import {
  purchaseReturnSchema,
  purchaseReturnValidation,
} from "components/Form/Purchase/Return/schema";

import { useActivityLog } from "hooks/use-activity-log";
import {
  ActivityLogDocumentType,
  PurchaseReturnFindUniqueQuery,
  usePurchaseReturnFindUniqueQuery,
} from "generated/purchase";
import { IBreadcrumbsAndMenu, ITab } from "types/global";
import { IPurchaseReturn } from "types/Purchase/purchaseReturn";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";

const PurchaseReturnContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const graphQLClient = createGraphQLClientWithMiddleware("purchase");
  const {
    state: { permissions },
  } = useStateContext();

  const methods = useForm<IPurchaseReturn>({
    defaultValues: { ...purchaseReturnSchema },
    resolver: yupResolver<any>(purchaseReturnValidation),
  });

  const { data, isLoading } =
    usePurchaseReturnFindUniqueQuery<PurchaseReturnFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  const { openActivityLog, onActivityLogOpen, onActivityLogClose } =
    useActivityLog();

  const { control } = methods;

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  const tabs: ITab[] = [
    {
      label: t("purchase.document_info"),
      path: `${pathname}`,
    },
    data?.purchaseReturnFindUnique?.aggrid_status === "finished"
      ? {
          label: "ใบ" + t("inventory.goods_issue.index"),
          path: `${pathname}?tab=goods_issue`,
        }
      : null,
  ].filter(Boolean) as ITab[];

  const renderTab = (tab: string | null) => {
    switch (tab) {
      case "goods_issue":
        return (
          <GoodsIssueTab
            referenceUniqueId={data?.purchaseReturnFindUnique.unique_id}
            isLoading={isLoading}
          />
        );
      default:
        return <DocumentInfoTab />;
    }
  };

  const currentTab = pathname + (tab ? `?tab=${tab}` : "");

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("purchase.index"),
      to: "/purchase",
    },
    {
      name: t("purchase.return.index"),
      to: "/purchase/return",
    },
    {
      name: id
        ? data?.purchaseReturnFindUnique.unique_id || "-"
        : t("button.create") + t("purchase.return.index"),
    },
  ];

  if (permissions && !permissions.purchase_return?.view) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <FormProvider {...methods}>
      <BreadcrumbsLayout
        breadcrumbs={breadcrumbs}
        onActivityLogOpen={onActivityLogOpen}
      />
      {(tab || data?.purchaseReturnFindUnique.unique_id) && (
        <CustomizedTab tabs={tabs} currentTab={currentTab} divider />
      )}
      {renderTab(tab)}
      {id && (
        <ActivityLog
          open={openActivityLog}
          onClose={onActivityLogClose}
          documentId={id!}
          documentType={ActivityLogDocumentType.PurchaseReturn}
        />
      )}
    </FormProvider>
  );
};

export default PurchaseReturnContainer;
